import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
	fetchOnboarding,
	fetchProOnboarding,
	fetchSubscriptionSummary,
	cancelScheduledDowngrade,
	fetchUpgradeSeatSelection,
	makeApiRequest,
	openBillingPortal,
	reactivateSubscription,
	restoreOnboarding,
	restoreProOnboarding,
	storeActiveOrganization,
} from '../../../Shared/Api';
import { useAppContext, ACTION_TYPES } from '../../../Context/AppContext';
import {
	refreshOnboardingChecklist,
	refreshProOnboardingChecklist,
} from '../../../Shared/onboarding';
import { useFeature } from '../../../Hooks/useFeature';
import { clearJobStatusCache } from '../../../Hooks/useJobStatuses';
import { PRICING_TIERS } from '../../Landing/constants';
import { SETTINGS_SECTIONS, VENMO_MAX_AMOUNT } from '../constants';

const DEFAULT_FORM = {
	invoice_payee: '',
	invoice_mailing_address: '',
	venmo_username: '',
	from_email: '',
	phone: '',
	tax_id: '',
	default_itemized_bids: false,
	bid_reminder_interval_days: 2,
	invoice_reminder_interval_days: 2,
	acceptance_revocation_minutes: 15,
	shop_address: '',
	shop_latitude: '',
	shop_longitude: '',
	clock_in_radius_feet: 500,
	allow_clock_in_at_job_addresses: false,
	allow_clock_in_without_location: false,
	sms_alert_phone: '',
	sms_on_bid_accepted: false,
	sms_on_schedule_assigned: false,
	sms_on_schedule_changed: false,
	sms_on_clock_in: false,
	sms_on_clock_out: false,
	sms_on_geofence_failure: false,
	sms_on_bid_expired: false,
	sms_quiet_hours_enabled: true,
	sms_quiet_hours_start: '21:00',
	sms_quiet_hours_end: '06:00',
};

export default function useOrganizationSettings() {
	const { dispatch, state } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const [searchParams, setSearchParams] = useSearchParams();

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [connecting, setConnecting] = useState(false);
	const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
	const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
	const [upgradePlanModalOpen, setUpgradePlanModalOpen] = useState(false);
	const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [reactivating, setReactivating] = useState(false);
	const [cancelingScheduledDowngrade, setCancelingScheduledDowngrade] =
		useState(false);
	const [subscription, setSubscription] = useState(null);
	const [subscriptionLoading, setSubscriptionLoading] = useState(true);
	const [sendingSampleBid, setSendingSampleBid] = useState(false);
	const [sendingSampleInvoice, setSendingSampleInvoice] = useState(false);
	const [loadError, setLoadError] = useState('');
	const [organization, setOrganization] = useState(null);
	const [onboardingDismissed, setOnboardingDismissed] = useState(false);
	const [proOnboardingDismissed, setProOnboardingDismissed] = useState(false);
	const [restoringOnboarding, setRestoringOnboarding] = useState(false);
	const [restoringProOnboarding, setRestoringProOnboarding] = useState(false);
	const [formData, setFormData] = useState(DEFAULT_FORM);
	const [activeSection, setActiveSection] = useState(null);

	const isManager =
		state.user.role === 'Manager' || Boolean(state.user.isSuperuser);

	const canUseTimeTracking = useFeature('time_tracking');
	const canUseSmsNotifications = useFeature('sms_notifications');

	const stripeReady = organization?.can_accept_stripe_payments;
	const stripeConnected = organization?.stripe_connected;
	const stripePending =
		stripeConnected && !organization?.stripe_details_submitted;

	const currentPlanTier = PRICING_TIERS.find(
		(tier) =>
			tier.id === (subscription?.plan || organization?.subscription_plan),
	);
	const subscriptionStatus =
		subscription?.status || organization?.subscription_status;
	const subscriptionExempt = Boolean(
		subscription?.subscription_exempt ?? organization?.subscription_exempt,
	);
	const showSubscriptionSection = isManager;
	const showBillingManagedManually = isManager && subscriptionExempt;

	const paymentPreview = useMemo(() => {
		const payee = formData.invoice_payee.trim();
		const address = formData.invoice_mailing_address.trim();
		if (!payee && !address) {
			return 'Check payment instructions will appear here as you fill out the form.';
		}
		if (!payee || !address) {
			return 'Complete both payee and mailing address to preview check instructions.';
		}
		return `Please make your check payable to ${payee} and mail it to ${address}.`;
	}, [formData.invoice_payee, formData.invoice_mailing_address]);

	const venmoPreview = useMemo(() => {
		const username = formData.venmo_username.trim().replace(/^@+/, '');
		if (!username) {
			return 'Add your Venmo username to offer Venmo on deposit and invoice emails.';
		}
		return `Customers will see a Pay with Venmo @${username} button on deposits and final invoices when the amount is $${VENMO_MAX_AMOUNT.toLocaleString()} or less. Amounts above that limit will not include Venmo.`;
	}, [formData.venmo_username]);

	const emailFooterPreview = useMemo(() => {
		const orgName = organization?.name?.trim() || 'Your business';
		const contactParts = [
			formData.from_email.trim(),
			formData.phone.trim(),
		].filter(Boolean);
		const lines = [orgName];
		if (contactParts.length) {
			lines.push(contactParts.join(' | '));
		}
		if (formData.tax_id.trim()) {
			lines.push(`EIN ${formData.tax_id.trim()}`);
		}
		return lines.join('\n');
	}, [
		organization?.name,
		formData.from_email,
		formData.phone,
		formData.tax_id,
	]);

	const syncOrganizationContext = useCallback(
		(org) => {
			if (!org?.id) {
				return;
			}
			if (state.user.isSuperuser) {
				storeActiveOrganization(org);
			}
			dispatch({
				type: ACTION_TYPES.SET_ACTIVE_ORGANIZATION,
				payload: org,
			});
		},
		[dispatch, state.user.isSuperuser],
	);

	const fetchOrganization = useCallback(async () => {
		setLoading(true);
		setLoadError('');
		try {
			const response = await makeApiRequest('GET', '/auth/my-organization/');
			setOrganization(response);
			syncOrganizationContext(response);
			setFormData({
				invoice_payee: response.invoice_payee || '',
				invoice_mailing_address: response.invoice_mailing_address || '',
				venmo_username: response.venmo_username || '',
				from_email: response.from_email || '',
				phone: response.phone || '',
				tax_id: response.tax_id || '',
				default_itemized_bids: Boolean(response.default_itemized_bids),
				bid_reminder_interval_days: response.bid_reminder_interval_days ?? 2,
				invoice_reminder_interval_days:
					response.invoice_reminder_interval_days ?? 2,
				acceptance_revocation_minutes:
					response.acceptance_revocation_minutes ?? 15,
				shop_address: response.shop_address || '',
				shop_latitude:
					response.shop_latitude != null ? String(response.shop_latitude) : '',
				shop_longitude:
					response.shop_longitude != null
						? String(response.shop_longitude)
						: '',
				clock_in_radius_feet: response.clock_in_radius_feet ?? 500,
				allow_clock_in_at_job_addresses: Boolean(
					response.allow_clock_in_at_job_addresses,
				),
				allow_clock_in_without_location: Boolean(
					response.allow_clock_in_without_location,
				),
				sms_alert_phone: response.sms_alert_phone || '',
				sms_on_bid_accepted: Boolean(response.sms_on_bid_accepted),
				sms_on_schedule_assigned: Boolean(response.sms_on_schedule_assigned),
				sms_on_schedule_changed: Boolean(response.sms_on_schedule_changed),
				sms_on_clock_in: Boolean(response.sms_on_clock_in),
				sms_on_clock_out: Boolean(response.sms_on_clock_out),
				sms_on_geofence_failure: Boolean(response.sms_on_geofence_failure),
				sms_on_bid_expired: Boolean(response.sms_on_bid_expired),
				sms_quiet_hours_enabled:
					response.sms_quiet_hours_enabled !== undefined
						? Boolean(response.sms_quiet_hours_enabled)
						: true,
				sms_quiet_hours_start: response.sms_quiet_hours_start
					? String(response.sms_quiet_hours_start).slice(0, 5)
					: '21:00',
				sms_quiet_hours_end: response.sms_quiet_hours_end
					? String(response.sms_quiet_hours_end).slice(0, 5)
					: '06:00',
			});
		} catch (error) {
			setLoadError(error.message || 'Failed to load organization settings.');
		} finally {
			setLoading(false);
		}
	}, [syncOrganizationContext]);

	const fetchSubscription = useCallback(async () => {
		if (!isManager) {
			setSubscriptionLoading(false);
			return;
		}
		setSubscriptionLoading(true);
		try {
			const response = await fetchSubscriptionSummary();
			setSubscription(response);
		} catch {
			setSubscription(null);
		} finally {
			setSubscriptionLoading(false);
		}
	}, [isManager]);

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Settings' },
		});
		fetchOrganization();
		fetchSubscription();
	}, [dispatch, fetchOrganization, fetchSubscription]);

	useEffect(() => {
		if (!state.initialized || !isManager) {
			return;
		}
		fetchOnboarding()
			.then((response) => {
				setOnboardingDismissed(Boolean(response?.dismissed));
			})
			.catch(() => {
				setOnboardingDismissed(false);
			});
		fetchProOnboarding()
			.then((response) => {
				setProOnboardingDismissed(Boolean(response?.dismissed));
			})
			.catch(() => {
				setProOnboardingDismissed(false);
			});
	}, [isManager, state.initialized]);

	useEffect(() => {
		const stripeParam = searchParams.get('stripe');
		if (!stripeParam) {
			return;
		}
		fetchOrganization().then(() => {
			if (stripeParam === 'return') {
				enqueueSnackbar('Stripe setup updated.', { variant: 'success' });
			} else if (stripeParam === 'refresh') {
				enqueueSnackbar('Continue Stripe setup when you are ready.', {
					variant: 'info',
				});
			}
			setActiveSection(SETTINGS_SECTIONS.payments);
			refreshOnboardingChecklist();
			setSearchParams({ section: SETTINGS_SECTIONS.payments }, { replace: true });
		});
	}, [searchParams, setSearchParams, fetchOrganization, enqueueSnackbar]);

	useEffect(() => {
		const billingParam = searchParams.get('billing');
		if (!billingParam) {
			return;
		}
		Promise.all([fetchOrganization(), fetchSubscription()]).then(() => {
			if (billingParam === 'return') {
				enqueueSnackbar('Billing updated.', { variant: 'success' });
			}
			setActiveSection(SETTINGS_SECTIONS.billing);
			setSearchParams({ section: SETTINGS_SECTIONS.billing }, { replace: true });
		});
	}, [
		searchParams,
		setSearchParams,
		fetchOrganization,
		fetchSubscription,
		enqueueSnackbar,
	]);

	useEffect(() => {
		if (loading) {
			return;
		}
		const sectionParam = searchParams.get('section');
		if (
			sectionParam &&
			Object.values(SETTINGS_SECTIONS).includes(sectionParam)
		) {
			setActiveSection(sectionParam);
		} else if (
			!searchParams.get('onboarding') &&
			!searchParams.get('stripe') &&
			!searchParams.get('billing')
		) {
			setActiveSection(null);
		}
		const onboardingTarget = searchParams.get('onboarding');
		if (onboardingTarget === 'branding' || onboardingTarget === 'mailing') {
			setActiveSection(SETTINGS_SECTIONS.company);
			setSearchParams({ section: SETTINGS_SECTIONS.company }, { replace: true });
			return;
		}
		if (onboardingTarget === 'job-statuses') {
			setActiveSection(SETTINGS_SECTIONS.workflow);
			setSearchParams(
				{ section: SETTINGS_SECTIONS.workflow },
				{ replace: true },
			);
		}
	}, [loading, searchParams, setSearchParams]);

	const handleSectionChange = useCallback(
		(section) => {
			setActiveSection(section);
			setSearchParams({ section }, { replace: true });
		},
		[setSearchParams],
	);

	const handleClearSection = useCallback(() => {
		setActiveSection(null);
		setSearchParams({}, { replace: true });
	}, [setSearchParams]);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSave = async (event, overrides = {}) => {
		event?.preventDefault();
		setSaving(true);
		try {
			const payload = {
				...formData,
				...overrides,
				shop_latitude: formData.shop_latitude
					? Number(formData.shop_latitude)
					: null,
				shop_longitude: formData.shop_longitude
					? Number(formData.shop_longitude)
					: null,
				clock_in_radius_feet: Number(formData.clock_in_radius_feet) || 500,
				sms_quiet_hours_start: formData.sms_quiet_hours_start
					? `${String(formData.sms_quiet_hours_start).slice(0, 5)}:00`
					: '21:00:00',
				sms_quiet_hours_end: formData.sms_quiet_hours_end
					? `${String(formData.sms_quiet_hours_end).slice(0, 5)}:00`
					: '06:00:00',
			};
			// Consent is only meaningful when SMS section requests it.
			if (!Object.prototype.hasOwnProperty.call(overrides, 'sms_alert_consent')) {
				delete payload.sms_alert_consent;
			}
			const response = await makeApiRequest(
				'PATCH',
				'/auth/my-organization/',
				payload,
			);
			setOrganization(response);
			syncOrganizationContext(response);
			refreshOnboardingChecklist();
			refreshProOnboardingChecklist();
			setOnboardingDismissed(false);
			setProOnboardingDismissed(false);
			enqueueSnackbar('Settings saved.', { variant: 'success' });
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to save settings.', {
				variant: 'error',
			});
		} finally {
			setSaving(false);
		}
	};

	const handleRestoreOnboarding = async () => {
		setRestoringOnboarding(true);
		try {
			const response = await restoreOnboarding();
			setOnboardingDismissed(Boolean(response?.dismissed));
			refreshOnboardingChecklist();
			enqueueSnackbar('Setup guide restored.', { variant: 'success' });
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not restore setup guide.', {
				variant: 'error',
			});
		} finally {
			setRestoringOnboarding(false);
		}
	};

	const handleRestoreProOnboarding = async () => {
		setRestoringProOnboarding(true);
		try {
			const response = await restoreProOnboarding();
			setProOnboardingDismissed(Boolean(response?.dismissed));
			refreshProOnboardingChecklist();
			enqueueSnackbar('Pro setup guide restored.', { variant: 'success' });
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not restore Pro setup guide.', {
				variant: 'error',
			});
		} finally {
			setRestoringProOnboarding(false);
		}
	};

	const handleConnectStripe = async () => {
		setConnecting(true);
		try {
			const response = await makeApiRequest(
				'POST',
				'/payments/stripe/connect/',
			);
			if (response?.url) {
				window.location.href = response.url;
				return;
			}
			enqueueSnackbar('Stripe did not return an onboarding URL.', {
				variant: 'error',
			});
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to start Stripe setup.', {
				variant: 'error',
			});
		} finally {
			setConnecting(false);
		}
	};

	const handleManageBilling = async () => {
		setOpeningBillingPortal(true);
		try {
			const response = await openBillingPortal();
			if (response?.url) {
				window.location.assign(response.url);
				return;
			}
			enqueueSnackbar('Stripe did not return a billing portal URL.', {
				variant: 'error',
			});
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to open billing portal.', {
				variant: 'error',
			});
		} finally {
			setOpeningBillingPortal(false);
		}
	};

	const handleUpgradeToPro = async () => {
		try {
			const selection = await fetchUpgradeSeatSelection();
			if (selection?.requires_seat_selection) {
				setUpgradeModalOpen(true);
				return;
			}
			setUpgradePlanModalOpen(true);
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to start upgrade.', {
				variant: 'error',
			});
		}
	};

	const handlePrepareDowngrade = () => {
		setDowngradeModalOpen(true);
	};

	const handleCancelSubscription = () => {
		setCancelModalOpen(true);
	};

	const handleReactivateSubscription = async () => {
		setReactivating(true);
		try {
			await reactivateSubscription();
			enqueueSnackbar('Subscription reactivated.', { variant: 'success' });
			fetchOrganization();
			fetchSubscription();
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not reactivate subscription.', {
				variant: 'error',
			});
		} finally {
			setReactivating(false);
		}
	};

	const handleCancelScheduledDowngrade = async () => {
		setCancelingScheduledDowngrade(true);
		try {
			await cancelScheduledDowngrade();
			enqueueSnackbar('Scheduled downgrade canceled.', { variant: 'success' });
			fetchOrganization();
			fetchSubscription();
		} catch (error) {
			enqueueSnackbar(
				error.message || 'Could not cancel scheduled downgrade.',
				{ variant: 'error' },
			);
		} finally {
			setCancelingScheduledDowngrade(false);
		}
	};

	const handleBillingFlowSuccess = () => {
		fetchOrganization();
		fetchSubscription();
		refreshProOnboardingChecklist();
	};

	const handleSendSampleBid = async () => {
		const recipientEmail = state.user.email?.trim();
		if (!recipientEmail) {
			return;
		}
		setSendingSampleBid(true);
		try {
			const response = await makeApiRequest(
				'POST',
				'/auth/my-organization/send-sample-bid/',
			);
			enqueueSnackbar(
				response?.detail || `Sample bid sent to ${recipientEmail}.`,
				{ variant: 'success' },
			);
			refreshOnboardingChecklist();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to send sample bid.', {
				variant: 'error',
			});
		} finally {
			setSendingSampleBid(false);
		}
	};

	const handleSendSampleInvoice = async () => {
		const recipientEmail = state.user.email?.trim();
		if (!recipientEmail) {
			return;
		}
		setSendingSampleInvoice(true);
		try {
			const response = await makeApiRequest(
				'POST',
				'/auth/my-organization/send-sample-invoice/',
			);
			enqueueSnackbar(
				response?.detail || `Sample invoice sent to ${recipientEmail}.`,
				{ variant: 'success' },
			);
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to send sample invoice.', {
				variant: 'error',
			});
		} finally {
			setSendingSampleInvoice(false);
		}
	};

	const handleOrganizationUpdated = (updated) => {
		setOrganization(updated);
		syncOrganizationContext(updated);
		refreshOnboardingChecklist();
	};

	const handleJobStatusSuccess = (message) => {
		clearJobStatusCache();
		enqueueSnackbar(message, { variant: 'success' });
	};

	const sampleEmailTooltip = state.user.email
		? `Will be sent to ${state.user.email}`
		: 'Add an email address to your account first';

	let stripeStatusMessage =
		'Connect Stripe to include a pay-online link on invoices.';
	if (stripePending) {
		stripeStatusMessage =
			'Finish Stripe setup to start accepting card payments on invoices.';
	} else if (stripeConnected) {
		stripeStatusMessage =
			'Stripe is connected but not ready to accept payments yet. Complete onboarding or check your Stripe dashboard.';
	}

	return {
		state,
		loading,
		saving,
		connecting,
		openingBillingPortal,
		upgradeModalOpen,
		setUpgradeModalOpen,
		upgradePlanModalOpen,
		setUpgradePlanModalOpen,
		downgradeModalOpen,
		setDowngradeModalOpen,
		cancelModalOpen,
		setCancelModalOpen,
		reactivating,
		cancelingScheduledDowngrade,
		subscription,
		subscriptionLoading,
		sendingSampleBid,
		sendingSampleInvoice,
		loadError,
		organization,
		onboardingDismissed,
		proOnboardingDismissed,
		restoringOnboarding,
		restoringProOnboarding,
		formData,
		setFormData,
		activeSection,
		isManager,
		canUseTimeTracking,
		canUseSmsNotifications,
		stripeReady,
		stripeConnected,
		stripePending,
		currentPlanTier,
		subscriptionStatus,
		subscriptionExempt,
		showSubscriptionSection,
		showBillingManagedManually,
		paymentPreview,
		venmoPreview,
		emailFooterPreview,
		sampleEmailTooltip,
		stripeStatusMessage,
		handleSectionChange,
		handleClearSection,
		handleInputChange,
		handleSave,
		handleRestoreOnboarding,
		handleRestoreProOnboarding,
		handleConnectStripe,
		handleManageBilling,
		handleUpgradeToPro,
		handlePrepareDowngrade,
		handleCancelSubscription,
		handleReactivateSubscription,
		handleCancelScheduledDowngrade,
		handleBillingFlowSuccess,
		handleSendSampleBid,
		handleSendSampleInvoice,
		handleOrganizationUpdated,
		handleJobStatusSuccess,
		enqueueSnackbar,
	};
}
